import * as React from 'react';

import { cn } from '../../../lib/utils';
import { StatePlaceholder } from '../state-placeholder';

import { comingSoonPlaceholderTestId } from './coming-soon-placeholder-test-ids';

type IconComponent = React.ElementType<{ className?: string }>;

export interface ComingSoonPlaceholderProps
  // `role` is omitted deliberately: nothing here arrives asynchronously, so there is nothing
  // for a live region to announce.
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'role'> {
  /** Title displayed above the description */
  title: string;
  /** Description text explaining what the feature will do */
  description: string;
  /** Footer message displayed at the bottom (e.g., "Coming soon...") */
  footerMessage: string;
  /** Optional icon component from lucide-react */
  icon?: IconComponent;
  /** Optional custom icon element */
  iconElement?: React.ReactNode;
}

/** Neutral block for the call sites that ship no icon at all (the billing tabs). */
const ComingSoonIconFallback = () => (
  <div className="size-full rounded bg-muted-foreground/30" />
);

/**
 * A placeholder for features that are not built yet: centered icon, title, description and a
 * footer note.
 *
 * Kept on plain props rather than composed slots. Every one of its call sites passes the same
 * four strings and an icon — that is closed, data-shaped configuration, and turning it into
 * JSX slots would add ceremony without adding a single caller-varying section. Internally it
 * composes `StatePlaceholder`, so it shares the anatomy of its `restricted` and `not-found`
 * siblings.
 *
 * It announces no live-region role: nothing here arrives asynchronously, so there is nothing
 * to announce.
 */
export const ComingSoonPlaceholder = React.forwardRef<
  HTMLDivElement,
  ComingSoonPlaceholderProps
>(
  (
    {
      title,
      description,
      footerMessage,
      icon: Icon,
      iconElement,
      className,
      ...props
    },
    ref,
  ) => (
    <StatePlaceholder
      ref={ref}
      density="page"
      testId={comingSoonPlaceholderTestId}
      // Nothing here replaces a control the reader was using, so there is nothing to catch
      // focus. The `page` density only supplies the tile size and type scale.
      focusable={false}
      {...props}
      className={cn(
        'min-h-0 gap-4 rounded-md border bg-[#F9FAFB] p-12 shadow-none dark:bg-card',
        className,
      )}
    >
      <StatePlaceholder.Icon
        className="text-muted-foreground"
        tileClassName="bg-card"
      >
        {/* Truthy, not `??`: `ReactNode` admits `false`, and `iconElement={cond && <X/>}`
            must fall back to the icon rather than render an empty tile. */}
        {iconElement ? (
          iconElement
        ) : Icon ? (
          <Icon />
        ) : (
          <ComingSoonIconFallback />
        )}
      </StatePlaceholder.Icon>

      {/*
        No `Content` wrapper and a `p` title on purpose: the three lines sit at one even
        rhythm here, and a "coming soon" panel lives inside a page that already owns its
        heading.
      */}
      <StatePlaceholder.Title as="p" className="shrink-0 font-bold">
        {title}
      </StatePlaceholder.Title>

      <StatePlaceholder.Description className="max-w-none shrink-0 text-base font-normal leading-6 text-foreground">
        {description}
      </StatePlaceholder.Description>

      <StatePlaceholder.Footer className="shrink-0 text-base font-normal leading-6 text-foreground">
        {footerMessage}
      </StatePlaceholder.Footer>
    </StatePlaceholder>
  ),
);

ComingSoonIconFallback.displayName = 'ComingSoonPlaceholder.IconFallback';
ComingSoonPlaceholder.displayName = 'ComingSoonPlaceholder';
